// Detection and frequency rules for the outside-UK notice
// (client/src/components/common/OutsideUKNotice.tsx).
//
// Kept as a plain module rather than hook-internal state so all of it is
// testable without mounting React, and so the two decisions that matter live
// in one place with their reasons attached.
//
// DETECTION: THE DEVICE'S OWN TIME ZONE, NOT THE BACKEND.
//
// `Intl.DateTimeFormat().resolvedOptions().timeZone` is answered by the device
// with no round trip, so this works offline, on the first paint, and without
// waiting on any request. It is also nobody else's dependency: the customer
// timezone column being added on the backoffice side is a separate thing and
// this must not wait for it.
//
// IT FAILS CLOSED, AND THAT DIRECTION IS THE WHOLE POINT.
//
// If `Intl` throws, is missing, or returns an empty string, we show NOTHING. A
// missing time zone is not evidence that somebody is abroad, and the cost of
// the two errors is not symmetric: a false negative is a customer who does not
// see a notice, while a false positive tells a London customer that her own
// country is unsupported. Only a zone we actually read, and which is not
// London, counts as evidence.
//
// STORAGE: localStorage, for the same reasons as lib/app-banner.ts. It is a
// device-local UI preference that never needs to reach the server, and
// remembering "this person has seen a notice twice" is strictly functional
// rather than a consent question. Every access is wrapped, because Safari
// private browsing and lockdown settings can make even READING
// `window.localStorage` throw.
//
// FAILING OPEN ON STORAGE IS DELIBERATE TOO, and it is the opposite direction
// from the detection above. If storage is unreadable we treat the count as
// zero, so the notice shows. The worst case is a customer with storage
// disabled seeing it more than three times; the alternative is suppressing it
// for everyone whose browser locks storage down, which loses the feature for
// exactly the people most likely to be browsing privately.

// The UK zone list and the detection predicate live in @cch/shared, so the two
// clients cannot hold different ideas of which zones are British.
//
// Imported AND re-exported: a bare `export { x } from` does not bind `x` in
// this module's scope, and `shouldShowNotice` below calls it.
import { isOutsideUK, type TimeZoneReader } from "@cch/shared/uk-timezones";

export {
  UK_TIMEZONE,
  UK_TIMEZONES,
  isUKTimeZone,
  isOutsideUK,
  type TimeZoneReader,
} from "@cch/shared/uk-timezones";

/** Adrian's rule: three times, ever. */
export const MAX_SHOWS = 3;

const COUNT_KEY = "cch.uk-notice.shows";
const SUPPRESSED_KEY = "cch.uk-notice.suppressed";

/** Storage that may not exist and may throw on read. */
export interface NoticeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function storage(): NoticeStorage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/** How many times it has been shown. Unreadable storage counts as zero. */
export function shownCount(store: NoticeStorage | null = storage()): number {
  try {
    const raw = store?.getItem(COUNT_KEY);
    const value = Number.parseInt(raw ?? "", 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

/** True once the customer has ticked "do not show this again". */
export function isSuppressed(store: NoticeStorage | null = storage()): boolean {
  try {
    return store?.getItem(SUPPRESSED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Recorded when the notice is actually SHOWN, never when it is dismissed.
 *
 * A force quit, a crash or a closed tab all skip the dismissal. Counting on
 * dismissal would hand the customer a fresh view every time that happened,
 * which is how "at most three" becomes "as many as you like".
 */
export function recordShown(store: NoticeStorage | null = storage()): void {
  try {
    store?.setItem(COUNT_KEY, String(shownCount(store) + 1));
  } catch {
    // Storage refused the write. The notice was still shown; it simply will
    // not be remembered. See the note at the top about failing open.
  }
}

/** Ticking the box stops it immediately, regardless of the count. */
export function suppressForever(store: NoticeStorage | null = storage()): void {
  try {
    store?.setItem(SUPPRESSED_KEY, "1");
  } catch {
    // As above.
  }
}

/**
 * The one decision: should this visit show the notice?
 *
 * Both gates, in the order that makes the reason readable: suppressed at all,
 * then over the cap, then actually abroad. Written as one function so the two
 * platforms cannot drift apart on the rule.
 */
export function shouldShowNotice(options: {
  read?: TimeZoneReader;
  store?: NoticeStorage | null;
} = {}): boolean {
  const store = options.store === undefined ? storage() : options.store;
  if (isSuppressed(store)) return false;
  if (shownCount(store) >= MAX_SHOWS) return false;
  return isOutsideUK(options.read);
}
